/**
 * Client-side persistence of the "Offene Prüfungen" filter/sort state (TES-506).
 *
 * GAP 4 (siehe Issue-Body): Es existiert KEIN generischer per-User-Preference-
 * Store im Schema (`profiles` hat nur feste Spalten, kein freies JSON-Feld).
 * Persistenz daher client-seitig via localStorage. Eine echte serverseitige
 * "gespeicherte Sicht" gehört in F-CHK-FILT und ist ein eigener Issue.
 *
 * This module is intentionally framework-free and defensive (never throws on a
 * missing/blocked/corrupt localStorage) so it stays unit-testable.
 */

import type { InspectionStatus } from "@/lib/openInspectionsWindow";

/** Internal localStorage key (not part of the public surface). */
const OPEN_INSPECTIONS_FILTER_STORAGE_KEY = "checkmaty.openInspections.filters.v1";

export type OpenInspectionsSortKey = "due_date" | "status" | "created_at";

export interface OpenInspectionsFilterState {
  /** Group (Fachbereich) id, or 'all' for no group filter (TES-2017: Gruppe statt Team). */
  groupId: string;
  /** Inspection status, or 'all'. `cancelled`/`completed` are not offered. */
  status: InspectionStatus | "all";
  /** Checklist (Vorlage) id, or 'all'. */
  checklistId: string;
  /** Sort column. */
  sortBy: OpenInspectionsSortKey;
}

export const DEFAULT_OPEN_INSPECTIONS_FILTERS: OpenInspectionsFilterState = {
  groupId: "all",
  status: "all",
  checklistId: "all",
  sortBy: "due_date",
};

const VALID_SORT_KEYS: readonly OpenInspectionsSortKey[] = ["due_date", "status", "created_at"];
const VALID_STATUSES: readonly (InspectionStatus | "all")[] = [
  "all",
  "pending",
  "in_progress",
  "overdue",
  "completed",
  "cancelled",
];

/**
 * Validate + coerce an arbitrary parsed object into a complete filter state.
 * Unknown/invalid fields fall back to the defaults — never returns partial state.
 * Internal helper of {@link loadFilterState} (tested through load/save).
 */
function normalizeFilterState(
  raw: Partial<OpenInspectionsFilterState> | null | undefined,
): OpenInspectionsFilterState {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_OPEN_INSPECTIONS_FILTERS };
  return {
    groupId: typeof raw.groupId === "string" && raw.groupId ? raw.groupId : "all",
    status:
      typeof raw.status === "string" &&
      VALID_STATUSES.includes(raw.status as InspectionStatus | "all")
        ? (raw.status as InspectionStatus | "all")
        : "all",
    checklistId: typeof raw.checklistId === "string" && raw.checklistId ? raw.checklistId : "all",
    sortBy:
      typeof raw.sortBy === "string" &&
      VALID_SORT_KEYS.includes(raw.sortBy as OpenInspectionsSortKey)
        ? (raw.sortBy as OpenInspectionsSortKey)
        : "due_date",
  };
}

export function loadFilterState(
  storage: Pick<Storage, "getItem"> | undefined = safeStorage(),
): OpenInspectionsFilterState {
  if (!storage) return { ...DEFAULT_OPEN_INSPECTIONS_FILTERS };
  try {
    const stored = storage.getItem(OPEN_INSPECTIONS_FILTER_STORAGE_KEY);
    if (!stored) return { ...DEFAULT_OPEN_INSPECTIONS_FILTERS };
    return normalizeFilterState(JSON.parse(stored) as Partial<OpenInspectionsFilterState>);
  } catch {
    return { ...DEFAULT_OPEN_INSPECTIONS_FILTERS };
  }
}

export function saveFilterState(
  state: OpenInspectionsFilterState,
  storage: Pick<Storage, "setItem"> | undefined = safeStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(OPEN_INSPECTIONS_FILTER_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* localStorage unavailable / quota exceeded — non-fatal, filters stay in-memory. */
  }
}

/** Returns `window.localStorage` if reachable, otherwise `undefined` (SSR/blocked). */
function safeStorage(): Storage | undefined {
  try {
    if (typeof window === "undefined" || !window.localStorage) return undefined;
    return window.localStorage;
  } catch {
    return undefined;
  }
}
